/**
 * Static catalog of the integrations a business can connect.
 *
 * `key` and `credentialType` are the only coupling points with the backend:
 *  - `key` MUST equal a value in the backend allow-list (Services.Infrastructure.Integrations):
 *    'Shopify' | 'OngoingSystems' | 'SmartFulfilment' | 'eBay'.
 *  - `credentialType` MUST equal a backend CredentialType, and each `field.key` MUST match the
 *    PascalCase property name on the corresponding C# ICredentials class, so the connect handler
 *    can spread the form values straight onto the credential object.
 * Everything else is presentational and changes only when we ship UI.
 */

export type CredentialType =
  | 'ApiKey'
  | 'UsernamePassword'
  | 'Token'
  | 'OAuth'
  | 'Other';

export interface CredentialField {
  /** PascalCase C# property: 'ApiKey' | 'Username' | 'Password' | 'Token' | 'Data'. */
  key: string;
  label: string;
  /** 'warehouse' renders a picker of the business's contracted warehouses and submits its id. */
  type?: 'text' | 'password' | 'warehouse';
  placeholder?: string;
  /** Persistent guidance shown under the field (survives typing, unlike the placeholder). */
  helperText?: string;
  /** Connect stays enabled while this is blank; the backend field must be nullable. */
  optional?: boolean;
}

export interface IntegrationConfig {
  /** Backend allow-list value — also used to match against a credential's `Platform`. */
  key: string;
  name: string;
  description: string;
  category: string;
  /** Path under /images/integrations/*; falls back to an initial badge if missing. */
  logo: string;
  credentialType: CredentialType;
  fields: CredentialField[];
}

export const EBAY_PLATFORM = 'eBay';

export const INTEGRATIONS: IntegrationConfig[] = [
  {
    key: 'Shopify',
    name: 'Shopify',
    description:
      'Sync products, customers and orders from your Shopify store into Flox.',
    category: 'E-commerce',
    logo: '/images/integrations/shopify.svg',
    credentialType: 'ApiKey',
    fields: [
      {
        key: 'ClientId',
        label: 'Client ID',
        type: 'text',
        placeholder: 'e.g. 6f2a1b9c8d3e4f5a…',
        helperText: 'Shopify Dev Dashboard → your app → API credentials.',
      },
      {
        key: 'ApiKey',
        label: 'Client secret',
        type: 'password',
        placeholder: 'shpss_…',
        helperText: 'Shown beside the Client ID in the same app.',
      },
      {
        key: 'ShopDomain',
        label: 'Shop domain',
        type: 'text',
        placeholder: 'your-store.myshopify.com',
        helperText: 'The address you use to sign in to your Shopify admin.',
      },
    ],
  },
  {
    key: 'SmartFulfilment',
    name: 'SmartFulfilment',
    description:
      'Sync your product catalogue and stock levels from SmartFulfilment into Flox.',
    category: 'Fulfilment',
    logo: '/images/integrations/smartfulfilment.svg',
    credentialType: 'UsernamePassword',
    fields: [
      {
        key: 'Username',
        label: 'Email address',
        type: 'text',
        placeholder: 'you@yourcompany.com',
        helperText:
          'The email you use to sign in to the SmartFulfilment portal.',
      },
      {
        key: 'Password',
        label: 'Password',
        type: 'password',
        helperText:
          'Your SmartFulfilment portal password. Stored encrypted and used only to open a session.',
      },
      {
        key: 'ApiDomain',
        label: 'Portal address',
        type: 'text',
        placeholder: 'https://app.smartfulfilment.nl',
        optional: true,
        helperText:
          'Leave blank unless SmartFulfilment gave you a different address.',
      },
      {
        key: 'WarehouseId',
        label: 'Warehouse holding this stock',
        type: 'warehouse',
        helperText:
          'The contracted warehouse SmartFulfilment fulfils from. Stock levels cannot be synced without it, and it cannot be changed later without disconnecting.',
      },
    ],
  },
  {
    key: EBAY_PLATFORM,
    name: 'eBay',
    description:
      'Sync your listings, buyers and orders from your eBay seller account into Flox.',
    category: 'Marketplace',
    logo: '/images/integrations/ebay.svg',
    credentialType: 'OAuth',
    fields: [],
  },
];
